import { useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, X, MessageCircle } from "lucide-react";
import { BUSINESS } from "../lib/site";

const WA_MESSAGE = "Ciao, vorrei maggiori informazioni sugli appartamenti in Val di Sole";
const WA_LINK = `https://wa.me/${BUSINESS.phoneHref.replace("+", "")}?text=${encodeURIComponent(WA_MESSAGE)}`;
const TEL_LINK = `tel:${BUSINESS.phoneHref}`;

function WhatsAppIcon({ size = 20 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
  );
}

export default function QuickContact() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin")) return null;

  return (
    <div className="fixed bottom-20 left-5 z-[60] flex flex-col items-start gap-3" data-testid="quick-contact">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="flex flex-col gap-2.5"
          >
            <a
              href={WA_LINK}
              target="_blank"
              rel="noreferrer"
              data-testid="quick-contact-whatsapp"
              className="flex items-center gap-3 rounded-full bg-white shadow-xl border border-stone pl-3 pr-5 py-2.5 hover:scale-[1.03] transition-transform"
            >
              <span className="h-9 w-9 rounded-full bg-[#25D366] text-white flex items-center justify-center shrink-0"><WhatsAppIcon size={19} /></span>
              <span className="text-left leading-tight">
                <span className="block font-display text-base text-fir">WhatsApp</span>
                <span className="block text-[11px] text-fir-deep/60">Rispondiamo subito</span>
              </span>
            </a>
            <a
              href={TEL_LINK}
              data-testid="quick-contact-call"
              className="flex items-center gap-3 rounded-full bg-white shadow-xl border border-stone pl-3 pr-5 py-2.5 hover:scale-[1.03] transition-transform"
            >
              <span className="h-9 w-9 rounded-full bg-fir text-gold flex items-center justify-center shrink-0"><Phone size={18} /></span>
              <span className="text-left leading-tight">
                <span className="block font-display text-base text-fir">Chiama ora</span>
                <span className="block text-[11px] text-fir-deep/60">{BUSINESS.phone}</span>
              </span>
            </a>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        data-testid="quick-contact-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Chiudi contatti rapidi" : "Contattaci prima di prenotare"}
        className={open
          ? "h-14 w-14 rounded-full bg-fir text-gold shadow-2xl flex items-center justify-center border border-wood/40 hover:scale-105 transition-transform"
          : "relative flex items-center gap-2.5 rounded-full bg-[#25D366] text-white pl-3.5 pr-5 py-3 shadow-2xl hover:scale-105 transition-transform"}
      >
        {open ? (
          <X size={24} />
        ) : (
          <>
            <span className="absolute -right-1 -top-1 h-3.5 w-3.5 rounded-full bg-gold animate-ping" />
            <WhatsAppIcon size={22} />
            <span className="font-semibold text-sm hidden sm:block">Contattaci</span>
          </>
        )}
      </button>
    </div>
  );
}
